import { Box, Typography, Alert } from '@mui/material';
import Papa from 'papaparse';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCsvUpload } from '../providers';

export interface ValidatorComponentProps<T, S = unknown> {
  csvParseResult?: Papa.ParseResult<string[]> | null | undefined;
  onError: (error: string) => void;
  onValidData: (data: T[] | undefined | null) => void;
  onInvalidData: () => void;
  state: S | undefined | null;
  setState: (state: S | undefined | null) => void;
}

export interface ValidateCsvStepProps<T, S> {
  ValidatorComponent: React.ComponentType<ValidatorComponentProps<T, S>>;
}

export const ValidateCsvStep = <T, S>({ ValidatorComponent }: ValidateCsvStepProps<T, S>) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const { csvParseResults, setValidationState, validationState, setResults } = useCsvUpload<T, S>();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('csvImport.steps.confirm.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('csvImport.steps.confirm.description')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('csvImport.steps.confirm.validationErrors')}:
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      <ValidatorComponent
        csvParseResult={csvParseResults}
        onValidData={setResults}
        state={validationState}
        setState={setValidationState}
        onInvalidData={() => {
          setResults(undefined);
        }}
        onError={setError}
      />
    </Box>
  );
};
